/**
 * The person-level work profile (PRD §3.2): hours a week, days a week and which weekdays.
 *
 * Arbeitstage picker matches the reference: shown only when Tage/Woche is 1–4 (Mo–Fr toggles,
 * exact count required to save). At 5+ the picker is hidden and we persist the first N ISO
 * weekdays (DB forbids null, unlike the reference).
 *
 * Stunden/Woche is clamped by admin settings; the hint under the pair names the weekly max.
 */

import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { needsWeekdayPicker, type WorkProfile } from "@/components/employees/work-profile";
import { effectiveMaxWeeklyHours, weeklyHoursExceedWorkdayCap } from "@/shared/work-profile-limits";

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri"] as const;

export function WorkProfileFields({
  value,
  onChange,
  idPrefix,
  maxWeeklyHours = 80,
  maxWorkdaysPerWeek = 7,
  maxHoursPerWorkday = 10,
}: {
  value: WorkProfile;
  onChange: (next: WorkProfile) => void;
  idPrefix: string;
  maxWeeklyHours?: number;
  maxWorkdaysPerWeek?: number;
  maxHoursPerWorkday?: number;
}) {
  const { t } = useTranslation();
  const expected = Number(value.workdaysPerWeek);
  const showWeekdays = needsWeekdayPicker(expected);
  const hoursCap = effectiveMaxWeeklyHours(expected, {
    maxWeeklyHours,
    maxHoursPerWorkday,
  });
  const weekly = Number(value.weeklyHours);
  const hoursOverCap =
    Number.isFinite(weekly) &&
    Number.isFinite(expected) &&
    weeklyHoursExceedWorkdayCap(weekly, expected, maxHoursPerWorkday);

  function toggle(day: number) {
    const next = value.workWeekdays.includes(day)
      ? value.workWeekdays.filter((entry) => entry !== day)
      : [...value.workWeekdays, day].sort((a, b) => a - b);
    onChange({ ...value, workWeekdays: next });
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-hours`}>{t("employees.weeklyHours")}</Label>
          <Input
            id={`${idPrefix}-hours`}
            type="number"
            min={1}
            max={hoursCap}
            step="0.5"
            value={value.weeklyHours}
            onChange={(event) => onChange({ ...value, weeklyHours: event.target.value })}
            required
            aria-invalid={hoursOverCap || undefined}
          />
          {hoursOverCap && (
            <p className="text-xs text-destructive" data-testid={`${idPrefix}-hours-over-cap`}>
              {t("employees.weeklyHoursOverWorkdayCap", {
                max: hoursCap,
                perDay: maxHoursPerWorkday,
                days: expected,
              })}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-days`}>{t("employees.workdaysPerWeek")}</Label>
          <Input
            id={`${idPrefix}-days`}
            type="number"
            min={1}
            max={maxWorkdaysPerWeek}
            step="1"
            value={value.workdaysPerWeek}
            onChange={(event) => {
              const workdaysPerWeek = event.target.value;
              const nextDays = Number(workdaysPerWeek);
              const prevDays = Number(value.workdaysPerWeek);
              // Entering the picker (from ≥5 → 1–4): start empty so the user picks exactly N days.
              const enteringPicker = needsWeekdayPicker(nextDays) && !needsWeekdayPicker(prevDays);
              const nextCap = effectiveMaxWeeklyHours(nextDays, {
                maxWeeklyHours,
                maxHoursPerWorkday,
              });
              const currentHours = Number(value.weeklyHours);
              const clampedHours =
                Number.isFinite(currentHours) && currentHours > nextCap
                  ? String(nextCap)
                  : value.weeklyHours;
              onChange({
                ...value,
                workdaysPerWeek,
                weeklyHours: clampedHours,
                workWeekdays: enteringPicker ? [] : value.workWeekdays,
              });
            }}
            required
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid={`${idPrefix}-hours-cap-hint`}>
        {t("employees.weeklyHoursCapHint", { max: maxWeeklyHours })}
      </p>

      {showWeekdays && (
        <div className="space-y-2">
          <Label>{t("employees.workWeekdays")}</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {WEEKDAY_KEYS.map((key, index) => {
              const day = index + 1;
              const active = value.workWeekdays.includes(day);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggle(day)}
                  className={cn(
                    "h-9 w-12 rounded-md border text-sm transition",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background hover:bg-muted",
                  )}
                >
                  {t(`weekdaysShort.${key}`)}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("employees.workWeekdaysHint", {
              count: expected,
              selected: value.workWeekdays.length,
            })}
          </p>
        </div>
      )}
    </>
  );
}
